import deepFreeze from 'deep-freeze-strict';
import queryString from 'query-string';
import _isEmpty from 'lodash/isEmpty';
import { window } from './globals';

type Id = string | number;

export interface FhirUrlOptions {
  account: string;
  id?: Id;
  params?: any;
  isPostQuery?: boolean;
}

export interface EvaluatedPolicyUrlOptions {
  user?: string;
}

// `urls.ts` is imported in both browser and Jest/node contexts. In tests we may
// not have a fully-populated `window.location`, so guard access.
const encodedOrigin = encodeURIComponent(window?.location?.origin ?? '');

export interface EvaluatedPolicyParams {
  user?: string;
}

export type DateTimeQuery = {
  startDateTime?: string;
  endDateTime?: string;
};

export type PatientFilesParams = {
  cohort: string;
} & object;

const urls = {
  oauth: {
    token: () => '/api/v1/oauth/token',
  },
  api: {
    accounts: {
      accounts: () => '/api/v1/accounts',
      logo: (id: Id) => `/api/v1/accounts/${id}/logo`,
      account: (id: Id) => `/api/v1/accounts/${id}`,
      groups: ({ id, params }: { id?: Id; params?: any } = {}) =>
        `/api/v1/account/groups${id ? '/' + id : ''}${stringifyParams(params)}`,
      groupMembership: (groupId: Id, params: object) =>
        `/api/v1/account/groups/${groupId}/members${stringifyParams(params)}`,
      groupMembershipAddDelete: (groupId: Id, userId: Id) =>
        `/api/v1/account/groups/${groupId}/members/${userId}`,
      accountUser: (userId: Id) => `/api/v1/account/users/${userId}`,
      reseller: {
        managedAccounts: () => '/api/v1/account-management/managed-accounts',
        managedAccountDetails: (id: Id) =>
          `/api/v1/account-management/managed-accounts/${id}`,
        tiles: () => '/api/v1/account-management/app-tiles',
      },
    },
    agents: {
      suggestActionPlan: () => '/api/v1/agents-v2/action-plan/invoke',
      generateExecutiveSummary: () => '/api/v1/agents-v2/health-summary/invoke',
      suggestEdit: () => '/api/v1/agents-v2/suggest/invoke',
      executeTemplate: () => `/api/v1/agents-v2/template-agent/invoke`,
      feedback: () => '/api/v1/agents-v2/feedback',
      iftttRules: () => '/api/v1/agents-v2/ifttt/rules',
      invocations: (agentName: string, subjectId: Id) =>
        `/api/v1/agents-v2/${agentName}/invocations?subject_id=${subjectId}`,
      invocation: (agentName: string, id: Id) =>
        `/api/v1/agents-v2/${agentName}/invocations/${id}`,
      assumeInvocation: (agentName: string, id: Id) =>
        `/api/v1/agents-v2/${agentName}/scheduled/${id}`,
      models: () => '/api/v1/agents-v2/models',
    },
    export: {
      insights: () => '/api/v1/data-export/insights',
    },
    wearables: (params: object, id?: Id) =>
      `/api/v1/wearables${id ? '/' + id : ''}${stringifyParams(params)}`,
    oauth: () => '/v1/oauth',
    products: () => '/api/v1/billing/products',
    checkoutPages: (creationToken?: string) =>
      `/api/v1/billing/checkout-pages${
        creationToken ? '/' + creationToken : ''
      }?uiVersion=${encodedOrigin}`,
    paymentMethodPages: () =>
      `/api/v1/billing/payment-method-pages?uiVersion=${encodedOrigin}`,
    usages: () => '/api/v1/usages',
    usageReports: (relativeUri: string, params = {}) =>
      `/api${relativeUri}${stringifyParams(params)}`,
    accountAuditing: (params: Record<string, unknown>) =>
      `/api/v1/audit/events${stringifyParams(params)}`,
    invoices: (id?: Id) => `/api/v1/invoices${id ? '/' + id : ''}`,
    customApps: () => '/api/v1/oauth-apps?appType=launched',

    rules: (id?: Id, params?: any) =>
      `/api/v1/rules${id ? '/' + id : ''}${stringifyParams(params)}`,
    content: {
      type: (account: string, type: string) =>
        `/api/v1/content/public/${account}?type=${type}`,
      saveDraft: (project: string) => `/api/v1/content/${project}/content`,
      publishDraft: (project: string, id: string) =>
        `/api/v1/content/${project}/content/${id}/publish`,
      archive: (project: string, id: string) =>
        `/api/v1/content/${project}/content/${id}/archive`,
      content: (project: string, id: string) =>
        `/api/v1/content/${project}/content/${id}`,
      v2: {
        create: () => '/api/v2/content/entity',
        update: (id: string) => `/api/v2/content/entity/${id}`,
        delete: (id: string) => `/api/v2/content/entity/${id}`,
        owned: (projectId: string) =>
          `/api/v2/content/entity/owner/project/${projectId}`,
        all: () => `/api/v2/content/entity/user`,
        assignment: () => `/api/v2/content/entity/assignment`,
        listAssignmentsForOwner: (projectId: string) =>
          `/api/v2/content/entity/assignment/owner/project/${projectId}`,
      },
    },
    fhirSearch: {
      projects: (projectId: Id, params?: any) =>
        `/api/v1/fhir-search/projects/${projectId}${stringifyParams(params)}`,
      projectsDistinct: (projectId: Id, params?: any) =>
        `/api/v1/fhir-search/projects/${projectId}/distinct${stringifyParams(
          params,
        )}`,
      cohort: (projectId: Id, cohortId: Id, params?: any) =>
        `/api/v1/fhir-search/projects/${projectId}/cohorts/${cohortId}${stringifyParams(
          params,
        )}`,
      cohortDistinct: (projectId: Id, cohortId: Id, params?: any) =>
        `/api/v1/fhir-search/projects/${projectId}/cohorts/${cohortId}/distinct${stringifyParams(
          params,
        )}`,
      patient: (projectId: Id, patientId: string, params?: any) =>
        `/api/v1/fhir-search/projects/${projectId}/patients/${patientId}${stringifyParams(
          params,
        )}`,
    },
    jobs: {
      jobs: (id?: Id, params?: any) =>
        `/api/v1/rules/jobs${id ? '/' + id : ''}${stringifyParams(params)}`,
    },
    workbookIngest: {
      ingest: (project: string) =>
        `/api/v1/workbook-ingest/projects/${project}/ingest`,
      template: () => `/api/v1/workbook-ingest/template`,
      history: (project: string, params?: any) =>
        `/api/v1/workbook-ingest/projects/${project}/history${stringifyParams(
          params,
        )}`,
      status: (project: string, params?: any) =>
        `/api/v1/workbook-ingest/projects/${project}/status${stringifyParams(
          params,
        )}`,
    },
    notes: {
      get: ({
        projectId,
        patientId,
        dateFrom,
        dateTo,
      }: {
        projectId: Id;
        patientId: Id;
        dateFrom: string;
        dateTo: string;
      }) =>
        `/api/v1/notes/projects/${projectId}/patient/${patientId}?date=gt${dateFrom}&date=lt${dateTo}`,
    },
    fhir: {
      bundleUpload: ({ account, params }: FhirUrlOptions, version = 'dstu3') =>
        `/fhir/${account}/${version}${stringifyParams(params)}`,
      baseFhirUrl: (_account: string, version = 'dstu3') =>
        `/api/v1/fhir/${version}/`,
      communication: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Communication${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      condition: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Condition${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      encounter: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Encounter${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      immunization: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Immunization${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      observation: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Observation${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      questionnaire: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Questionnaire${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      questionnaireResponse: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/QuestionnaireResponse${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      patient: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Patient${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      person: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Person${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      procedure: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Procedure${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      goal: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Goal${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      media: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Media${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      procedureRequest: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/ProcedureRequest${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      coverage: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Coverage${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      explanationOfBenefit: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/ExplanationOfBenefit${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      documentReference: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/DocumentReference${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      diagnosticReport: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/DiagnosticReport${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      referralRequest: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/ReferralRequest${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      specimen: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Specimen${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      consent: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Consent${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      imagingStudy: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/ImagingStudy${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      sequence: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/Sequence${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      medicationStatements: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/MedicationStatement${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      medicationAdministrations: ({
        id,
        params,
        isPostQuery,
      }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/MedicationAdministration${
          id ? '/' + id : ''
        }${isPostQuery ? '/_search' : stringifyParams(params)}`;
      },
      medicationRequests: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/MedicationRequest${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      medicationDispense: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/MedicationDispense${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      diagnosticReports: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/DiagnosticReport${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      me: () => `/api/v1/fhir/dstu3/$me`,
      carePlan: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/CarePlan${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
      clinicalImpression: ({ id, params, isPostQuery }: FhirUrlOptions) => {
        return `/api/v1/fhir/dstu3/ClinicalImpression${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`;
      },
    },
    surveys: {
      surveys: ({
        id,
        project,
        params,
      }: {
        id?: Id;
        project: Id;
        params?: object;
      }) =>
        `/api/v1/survey/projects/${project}/surveys${
          id ? `/${id}` : ''
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }${stringifyParams(params)}`,
      responses: ({
        id,
        project,
        params,
      }: {
        id?: Id;
        project: Id;
        params?: object;
      }) =>
        `/api/v1/survey/projects/${project}/responses${
          id ? `/${id}` : ''
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }${stringifyParams(params)}`,
      acceptOpenSurveyInvite: (inviteId: Id) =>
        `/api/v1/survey-invitations/accept/${inviteId}`,
      reminder: ({ surveyId, projectId }: { surveyId: Id; projectId: Id }) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/reminder`,
      previewData: (projectId: string, params: object) =>
        `/api/v1/survey/projects/${projectId}/responses/preview-data${stringifyParams(
          params,
        )}`,
      surveyImage: (projectId: string, surveyId: string, imageId?: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/image${
          imageId ? `/${imageId}` : ''
        }`,
      autopopulate: (projectId: string, cohortId?: string) =>
        `/api/v1/survey/projects/${projectId}/autopopulate${
          cohortId ? `?cohort=${cohortId}` : ''
        }`,
      templates: (projectId: string, surveyId: string, templateId?: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/templates${
          templateId ? `/${templateId}` : ''
        }`,
      testTemplate: (projectId: string, surveyId: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/templates/test`,
      responseTemplate: (
        projectId: string,
        surveyId: string,
        templateId: string,
        responseId: string,
      ) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/templates/${templateId}/responses/${responseId}`,
    },
    apiKeys: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/api-keys${id ? '/' + id : ''}${stringifyParams(params)}`,
    graphQL: () => '/api/v1/graphql',
    users: {
      user: () => '/api/v1/user',
      groups: (userId: Id, params: object) =>
        `/api/v1/users/${userId}/groups${stringifyParams(params)}`,
      groupsInAccount: (userId: Id, params: object) =>
        `/api/v1/account/users/${userId}/groups${stringifyParams(params)}`,
      groupMember: (user: Id, group: Id) =>
        `/api/v1/groups/${group}/members/${user}`,
      // Not including api as we are manually setting the base url as apiBaseUri
      picture: (user: Id) => `/v1/users/${user}/picture`,
      signature: (user: Id) => `/v1/users/${user}/signature`,
    },
    genomicIngestion: {
      fileAction: (projectId: string, id?: string, params?: any) =>
        `/api/v1/genomic-ingestion/projects/${projectId}/file-actions${
          id ? '/' + id : ''
        }${stringifyParams(params)}`,
      ingestions: (projectId: string, id?: string, params?: any) =>
        `/api/v1/genomic-ingestion/projects/${projectId}/ingestions${
          id ? '/' + id : ''
        }${stringifyParams(params)}`,
      reingest: (projectId: string) =>
        `/api/v1/genomic-ingestion/projects/${projectId}/ingestions/reingest`,
      ingestionPatientInfo: (projectId: string, id: string) =>
        `/api/v1/genomic-ingestion/projects/${projectId}/ingestions/${id}/patient-info`,
      germlineIngestions: (projectId: string, germlineIngestionId: string) =>
        `/api/v1/genomic-ingestion/projects/${projectId}/ingestions/germline-cases/${germlineIngestionId}`,
    },
    patientSummary: {
      qa: {
        curateConcepts: (projectId: string, patientId: string) =>
          `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/qa/concept-curation`,
        answer: (projectId: string, patientId: string) =>
          `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/qa/answer`,
      },
      demographicsPrelim: (projectId: string, patientId: string) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/demographics/prelim`,
      demographics: (projectId: string, patientId: string) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/demographics`,
      conditionsPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/conditions/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      conditions: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/conditions${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      observationsPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/observations/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      observations: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/observations${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      medicationsPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/medications/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      medications: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/medications${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      notesPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/notes/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      notes: (projectId: string, patientId: string, query?: DateTimeQuery) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/notes${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      proceduresPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/procedures/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      procedures: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/procedures${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      allPrelim: (
        projectId: string,
        patientId: string,
        query?: DateTimeQuery,
      ) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/all/prelim${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      all: (projectId: string, patientId: string, query?: DateTimeQuery) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/all${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          query,
        )}`,
      risks: (projectId: string, patientId: string) =>
        `/api/v1/patient-summaries/projects/${projectId}/patients/${patientId}/risks`,
    },
    files: {
      patientFile: (
        projectId: string,
        params: object & { cohort?: string; subject?: string },
        id?: Id,
      ) =>
        `/api/v1/fhir/files/projects/${projectId}/files${
          id ? '/' + id : ''
        }${stringifyParams(params)}`,
      listPatientFiles: (
        projectId: string,
        params: object & { cohort: string },
      ) =>
        `/api/v1/fhir/files/projects/${projectId}/list/files${stringifyParams(
          params,
        )}`,
      searchPatientFiles: (
        projectId: string,
        params: object & { cohort: string },
      ) =>
        `/api/v1/fhir/files/projects/${projectId}/search/files${stringifyParams(
          params,
        )}`,
      files: (params?: object, id?: Id) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/files${id ? '/' + id : ''}${stringifyParams(params)}`,
      transferJobs: (params: object, id?: Id) =>
        `/api/v1/files/transfer/jobs${id ? '/' + id : ''}${stringifyParams(
          params,
        )}`,
      transferJobsRun: (id: Id) => `/api/v1/files/transfer/jobs/${id}/run`,
    },
    clients: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      clients: (params: object = null, id?: Id) =>
        `/api/v1/clients${id ? '/' + id : ''}${stringifyParams(params)}`,
    },
    ontology: {
      bulkTraverseParents: (projectId: string) =>
        `/api/v1/terminology/projects/${projectId}/codings/parents/`,
      globalBulkTraverseParents: () =>
        `/api/v1/terminology/global/codings/parents/`,
      system: (project: string, id: string) =>
        `/api/v1/terminology/projects/${project}/code-systems/${id}`,
      systems: (project: string, params?: object) =>
        `/api/v1/terminology/projects/${project}/code-systems${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      localSystems: (project: string, params?: object) =>
        `/api/v1/terminology/projects/${project}/local/code-systems${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      associatedSystems: (id: string, params: object) =>
        `/api/v1/terminology/projects/${id}/associations/code-systems/${stringifyParams(
          params,
        )}`,
      valueSet: (project: string, id: string) =>
        `/api/v1/terminology/projects/${project}/value-sets/${id}`,
      valueSets: (project: string, params?: object) =>
        `/api/v1/terminology/projects/${project}/value-sets${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      localValueSets: (project: string, params?: object) =>
        `/api/v1/terminology/projects/${project}/local/value-sets${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      associatedValueSets: (id: string, params: object) =>
        `/api/v1/terminology/projects/${id}/associations/value-sets/${stringifyParams(
          params,
        )}`,
      codeSystemCodings: (
        projectId: string,
        systemId: string,
        params: object,
      ) =>
        `/api/v1/terminology/projects/${projectId}/code-systems/${systemId}/codings/${stringifyParams(
          params,
        )}`,
      valueSetCodings: (
        projectId: string,
        valueSetId: string,
        params?: object,
      ) =>
        `/api/v1/terminology/projects/${projectId}/value-sets/${valueSetId}/codings/${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      codings: (projectId: string, params: object) =>
        `/api/v1/terminology/projects/${projectId}/codings${stringifyParams(
          params,
        )}`,
      globalCodings: (params: object) =>
        `/api/v1/terminology/global/codings${stringifyParams(params)}`,
      traverseCodings: (projectId: string, codeId: string, params: object) =>
        `/api/v1/terminology/projects/${projectId}/codings/${codeId}/codings/${stringifyParams(
          params,
        )}`,
      codingsRelatedCodings: (
        projectId: string,
        systemId: string,
        codingId: string,
        params: object,
      ) =>
        `/api/v1/terminology/projects/${projectId}/code-systems/${systemId}/codings/${codingId}/codings${stringifyParams(
          params,
        )}`,
      modifyCodeSystemAssociation: (
        projectId: string,
        codeSystemId: string,
        params: object = {},
      ) =>
        `/api/v1/terminology/projects/${projectId}/associations/code-systems/${codeSystemId}${stringifyParams(
          params,
        )}`,
      modifyValueSetAssociation: (
        projectId: string,
        valueSetId: string,
        params: object = {},
      ) =>
        `/api/v1/terminology/projects/${projectId}/associations/value-sets/${valueSetId}${stringifyParams(
          params,
        )}`,
      projectCodings: (projectId: string) =>
        `/api/v1/terminology/projects/${projectId}/project-codings`,
    },
    patients: {
      patientsForProject: (projectId: string) =>
        `/api/v1/patient-mappings/projects/${projectId}`,
      uploadPatientFile: (projectId: string, subjectId: string) =>
        `/api/v1/fhir/files/projects/${projectId}/files?subject=${subjectId}`,
    },
    policies: {
      policies: (params?: object, id?: Id) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/policies${id ? '/' + id : ''}${stringifyParams(params)}`,
    },
    projects: {
      datasets: (params?: object, id?: Id) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/projects${id ? '/' + id : ''}${stringifyParams(params)}`,
      stats: () => '/api/v1/project-usages',
      patientCount: (params: object) =>
        `/phc/api/analytics/patient-count${stringifyParams(params, {
          arrayFormat: 'comma',
        })}`,
      files: (params: object, id: Id) =>
        `/api/v1/projects/${id}/files${stringifyParams(params)}`,
    },
    tags: (projectId: Id, tagId?: Id) =>
      `/phc/api/tags/${projectId}${tagId ? '/' + tagId : ''}`,
    analytics: {
      dsl: () => '/api/v1/analytics/dsl',
      configurations: (params: object, id?: Id) =>
        `/api/v1/analytics/configurations${id ? `/${id}` : ''}${stringifyParams(
          params,
        )}`,
      asyncDsl: (params?: object, id?: Id) =>
        `/api/v1/analytics/async-dsl${id ? `/${id}` : ''}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      allSummaries: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}${stringifyParams(params)}`,
      medicationSummary: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}/clinical/medication/counts${stringifyParams(
          params,
        )}`,
      procedureSummary: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}/clinical/procedure/counts${stringifyParams(
          params,
        )}`,
      conditionSummary: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}/clinical/condition/counts${stringifyParams(
          params,
        )}`,
      observationSummary: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}/clinical/observation/counts${stringifyParams(
          params,
        )}`,
      mediaSummary: (id: Id, params: object) =>
        `/api/v1/analytics/summary/${id}/clinical/media/counts${stringifyParams(
          params,
        )}`,
    },
    configuration: {
      layouts: (params: object, id?: Id) =>
        `/phc/api/configuration/layouts${id ? `/${id}` : ''}${stringifyParams(
          params,
        )}`,
    },
    subjects: {
      search: () => '/phc/api/subjects/search',
    },
    /** Zori AI KB ingestion status and on-demand sync (agents API; PHC-5792). */
    knowledgeBase: {
      syncState: (params: { project_id: string }) =>
        `/api/v1/agents-v2/knowledge-base/sync-state${stringifyParams(params)}`,
      syncNow: (params: {
        project_id: string;
        kind?: 'all' | 'members' | 'internal';
      }) =>
        `/api/v1/agents-v2/knowledge-base/sync-now${stringifyParams(params)}`,
      sync: () => '/api/v1/agents-v2/knowledge-base/sync',
    },
    survey: {
      surveyAdapter: (projectId: string, surveyId: string, name?: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/adapters${
          name ? `/${name}` : ''
        }`,
      globalAdapters: (projectId: string, surveyId: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/adapters/global`,
      surveySchedule: (projectId: string, surveyId: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/schedule`,
      surveyConfig: (projectId: string, surveyId: string) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/config`,
      surveyAttachment: (
        projectId: string,
        responseId: string,
        linkId: string,
        fileId?: string,
        params?: any,
      ) =>
        `/api/v1/survey/projects/${projectId}/responses/${responseId}/attachments/${linkId}${
          fileId ? `/${fileId}` : ''
        }${stringifyParams(params)}`,
      surveys: (projectId: string, id?: string, params?: any) =>
        `/api/v1/survey/projects/${projectId}/surveys${
          id ? `/${id}` : ''
        }${stringifyParams(params)}`,
      primarySurveyVersions: (
        projectId: string,
        primarySurveyId: string,
        params?: any,
      ) =>
        `/api/v1/survey/projects/${projectId}/surveys/${primarySurveyId}/versions${stringifyParams(
          params,
        )}`,
      versionSurvey: (projectId: string, surveyId: string, params?: any) =>
        `/api/v1/survey/projects/${projectId}/surveys/${surveyId}/versions${stringifyParams(
          params,
        )}`,
      surveyResponses: (projectId: string, id?: string, params?: any) =>
        `/api/v1/survey/projects/${projectId}/responses${
          id ? `/${id}` : ''
        }${stringifyParams(params)}`,
    },
    alert: (projectId: string, alertId?: string) =>
      `/api/v1/alerts/projects/${projectId}/${alertId}`,
    alerts: (projectId: string) => `/api/v1/alerts/projects/${projectId}`,
    knowledge: {
      biochemistry: (filterString?: string, params?: object) =>
        `/api/v1/knowledge/biochemistry${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}${filterString}`,
      evidence: (filterString?: string, params?: object) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/knowledge/evidence${stringifyParams(params)}${filterString}`,
      geneSets: (params?: object, id?: string) =>
        `/api/v1/knowledge/gene-sets${id ? '/' + id : ''}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      genes: (params?: object, id?: Id) =>
        `/api/v1/knowledge/genes${id ? '/' + id : ''}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      geneProfiles: (params?: object) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/knowledge/profiles${stringifyParams(params)}`,
      geneValidation: (params?: object, id?: Id) =>
        `/api/v1/knowledge/genes/validate${id ? '/' + id : ''}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      geneAggregates: ({ gene }: { gene: any }) =>
        `/api/v1/knowledge/expression/aggregates/${gene}`,
      pfamDataSets: (transcriptId: string) =>
        `/api/v1/knowledge/proteins?transcriptId=${transcriptId}`,
      variants: (transcriptId: string) =>
        `/api/v1/knowledge/variants?transcriptId=${transcriptId}`,
      drugClass: (params?: object) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/api/v1/knowledge/drugclass${stringifyParams(params)}`,
    },
    evaluatedPolicy: (params?: EvaluatedPolicyUrlOptions) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/evaluatedPolicy${stringifyParams(params)}`,
    tasks: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/tasks${id ? '/' + id : ''}${stringifyParams(params)}`,
    sftp: {
      users: (project: Id, params?: object, user?: Id) =>
        `/api/v1/sftp/projects/${project}/users${
          user ? '/' + user : ''
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }${stringifyParams(params)}`,
      userEvents: (project: Id, user: Id, params?: object) =>
        `/api/v1/sftp/projects/${project}/users/${user}/events${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
    },
    invitations: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/invitations${id ? '/' + id : ''}${stringifyParams(params)}`,
    cohorts: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/cohorts${id ? '/' + id : ''}${stringifyParams(params)}`,
    childCohorts: (projectId: Id, id: Id, params?: object) =>
      `/api/v1/cohorts/projects/${projectId}/cohorts/${id}/child-cohorts${stringifyParams(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        params,
      )}`,
    cohortsPatients: (projectId: Id, id: Id) =>
      `/api/v1/cohorts/projects/${projectId}/cohorts/${id}/patients`,
    features: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/features${id ? '/' + id : ''}${stringifyParams(params)}`,
    publicFeatures: (params?: object, id?: Id) =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      `/api/v1/features-public${id ? '/' + id : ''}${stringifyParams(params)}`,
    ga4gh: {
      variantsSearch: (account: Id) => `/ga4gh/${account}/v1/variants/search`,
      variantSets: (account: Id, id?: Id) =>
        `/ga4gh/${account}/v1/variantsets${id ? '/' + id : ''}`,
      variantSetsSearch: (account: Id) =>
        `/ga4gh/${account}/v1/variantsets/search`,
      readSets: (account: Id, id?: Id) =>
        `/ga4gh/${account}/v1/readgroupsets${id ? '/' + id : ''}`,
      readSetsSearch: (account: Id) =>
        `/ga4gh/${account}/v1/readgroupsets/search`,
      rnaSets: (account: Id, id?: Id) =>
        `/ga4gh/${account}/v1/rnaquantificationsets${id ? '/' + id : ''}`,
      rnaSetsSearch: (account: Id) =>
        `/ga4gh/${account}/v1/rnaquantificationsets/search`,
      copyNumberSets: (account: Id, id?: Id) =>
        `/ga4gh/${account}/v1/copynumbersets${id ? '/' + id : ''}`,
      structuralVariantSets: (account: Id, id?: Id) =>
        `/ga4gh/${account}/v1/fusionsets${id ? '/' + id : ''}`,
      genomicSets: (account: Id, id?: Id, params?: object) =>
        `/ga4gh/${account}/v1/genomicsets${id ? '/' + id : ''}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
    },
    auth: {
      login: (
        subdomain: string,
        redirectDomain: string,
        destination: string,
        search = {},
      ) =>
        `/auth/v1/login${stringifyParams({
          subdomain,
          redirectDomain,
          app: 'phc',
          destination,
          ...search,
        })}`,
      signup: (
        subdomain: string,
        redirectDomain: string,
        destination: string,
        search = {},
      ) =>
        `/auth/v1/signup${stringifyParams({
          subdomain,
          redirectDomain,
          app: 'phc',
          destination,
          ...search,
        })}`,
      redirectUri: () =>
        process.env.NODE_ENV === 'production'
          ? baseEnvUrl({ uri: '/auth/v1/app-redirect', window })
          : process.env.LOCAL_DEV_AUTH_REDIRECT_URL,
      logout: () => '/auth/v1/api/logout',
      cookie: () => '/auth/v1/api/cookie',
      clientIdWithSubdomain: (subdomain: string) =>
        `/auth/v1/api/client-id?subDomain=${subdomain}`,
    },
    consent: {
      projectConsentForms: (projectId: Id) =>
        `/api/v1/consent/projects/${projectId}/forms`,
      projectConsentFormVersion: (projectId: Id) =>
        `/api/v1/consent/projects/${projectId}/forms/version`,
      projectConsentFormById: (projectId: Id, formId: Id) =>
        `/api/v1/consent/projects/${projectId}/forms/${formId}`,
      projectConsents: (params: object) =>
        `/api/v1/consent/directives${stringifyParams(params)}`,
      consentDirective: (directiveId: Id) =>
        `/api/v1/consent/directives/${directiveId}`,
      patient: (projectId: Id, patientId: Id) =>
        `/api/v1/consent/projects/${projectId}/patients/${patientId}`,
      userConsents: (params: object, id?: Id) =>
        `/api/v1/consent/directives/me${id ? '/' + id : ''}${stringifyParams(
          params,
        )}`,
      consentForms: (params: object) =>
        `/api/v1/consent/forms${stringifyParams(params)}`,
    },
    genomics: {
      queryTests: ({
        projectId,
        patientId,
      }: {
        projectId: Id;
        patientId: Id;
      }) =>
        `/api/v1/genomics/projects/${projectId}/subjects/${patientId}/tests`,
      projectEvents: ({
        params,
        projectId,
      }: {
        params: object;
        projectId: Id;
      }) =>
        `/api/v1/genomics/projects/${projectId}/events${stringifyParams(
          params,
        )}`,
      projectTests: ({
        params,
        projectId,
      }: {
        params: object;
        projectId: Id;
      }) =>
        `/api/v1/genomics/projects/${projectId}/tests${stringifyParams(
          params,
        )}`,
      projectTest: ({
        projectId,
        testId,
        params,
      }: {
        projectId: Id;
        testId: Id;
        params?: object;
      }) =>
        `/api/v1/genomics/projects/${projectId}/tests/${testId}${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      getProjectResources: ({
        projectId,
        ...rest
      }: {
        projectId: Id;
        cohortId?: Id;
      }) =>
        `/api/v1/genomics/projects/${projectId}/resources${stringifyParams(
          rest,
        )}`,
      deleteTest: ({ projectId, testId }: { projectId: Id; testId: Id }) =>
        `/api/v1/genomics/projects/${projectId}/tests/${testId}`,
      variants: (params: object, id?: Id, isPostQuery?: boolean) =>
        `/api/v1/genomics/variants${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`,
      copyNumbers: (params: object, id?: Id, isPostQuery?: boolean) =>
        `/api/v1/genomics/copyNumbers${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`,
      structuralVariants: (params: object, id?: Id, isPostQuery?: boolean) =>
        `/api/v1/genomics/structural-variants${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`,
      expressions: (params: object, id?: Id, isPostQuery?: boolean) =>
        `/api/v1/genomics/expressions${id ? '/' + id : ''}${
          isPostQuery ? '/_search' : stringifyParams(params)
        }`,
      summary: (projectId: Id, testId: Id) =>
        `/api/v1/genomics/projects/${projectId}/tests/${testId}/summary`,
      ashionImport: () => '/api/v1/tasks/system/ashion-import',
      manifestPatientInfo: (projectId: Id, manifestId: Id) =>
        `/api/v1/genomics/manifest/${projectId}/${manifestId}`,
      manifestRun: () => '/api/v1/genomics/manifest/process',
      hydrateOmicsExplorer: ({
        projectId,
        subjectId,
      }: {
        projectId: string;
        subjectId: string;
      }) => `/phc/api/omics/hydrate?project=${projectId}&subject=${subjectId}`,
      createOmicsFilter: () => '/phc/api/omics-filters',
      updateOmicsFilter: (id: string) => `/phc/api/omics-filters/${id}`,
      deleteOmicsFilter: (id: string) => `/phc/api/omics-filters/${id}`,
      defaultOmicsFilter: () => '/phc/api/omics-filters/default',
    },
    ehrs: {
      ehrs: (id?: Id) => `/api/v1/ehrs${id ? '/' + id : ''}`,
      connectors: (ehrId: Id, id?: Id) =>
        `/api/v1/ehrs/${ehrId}/connectors${id ? '/' + id : ''}`,
      ingestions: (ehrId: Id, reingestAll?: boolean) =>
        `/api/v1/ehrs/${ehrId}/ingestions${
          reingestAll ? '?reingestAll=true' : ''
        }`,
      scheduledIngestions: (ehrId: Id, id?: Id) =>
        `/api/v1/ehrs/${ehrId}/scheduled-ingestions${id ? '/' + id : ''}`,
    },
    addData: () => '/phc/api/fhir/import',
    transcriptions: {
      getWebSocketURL: () => '/api/v1/transcriptions',
      comprehend: () => '/api/v1/transcriptions/comprehend',
      analyze: () => '/api/v1/transcriptions/analyze',
      persist: () => '/api/v1/transcriptions/persist',
    },
    synoptics: {
      analyze: (patientId: string) =>
        `/api/v1/synoptics/patients/${patientId}/analyze`,
      cardiovascularRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/cardiovascularrisk`,
        calculator: () => '/api/v1/synoptics/calculators/cardiovascularrisk',
      },
      diabeticRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/diabeticrisk`,
        calculator: () => '/api/v1/synoptics/calculators/diabeticrisk',
      },
      hIRRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/hirrisk`,
        calculator: () => '/api/v1/synoptics/calculators/hirrisk',
      },
      hypertensionRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/hypertensionrisk`,
        calculator: () => '/api/v1/synoptics/calculators/hypertensionrisk',
      },
      metabolicRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/metabolicrisk`,
        calculator: () => '/api/v1/synoptics/calculators/metabolicrisk',
      },
      tyGIRisk: {
        patient: (patientId: string) =>
          `/api/v1/synoptics/patients/${patientId}/calculator/tygirisk`,
        calculator: () => '/api/v1/synoptics/calculators/tygirisk',
      },
    },
    mtb: {
      publish: () => '/api/v1/mtb/notes',
      get: (params: object) => `/api/v1/mtb/notes${stringifyParams(params)}`,
      tempaltesUrl: `/api/v1/mtb/templates`,
      publishDraft: `/api/v1/mtb/draft/notes`,
      getQuestionnaireResponse: (params: object) =>
        `/api/v1/mtb/questionnaireResponse${stringifyParams(params)}`,
      draftResource: (status: string, params: object) =>
        `/api/v1/mtb/draft/notes/${status.concat(stringifyParams(params))}`,
      putDraft: (fileId: string) => `/api/v1/mtb/draft/notes/${fileId}`,
      getFile: (fileId: string, params: object) =>
        `/api/v1/mtb/file/${fileId.concat(stringifyParams(params))}`,
      notesByQuestionnaireResponseId: (
        questionnaireResponseId: string,
        params: object,
      ) =>
        `/api/v1/mtb/notes/questionnaireResponses/${questionnaireResponseId}${stringifyParams(
          params,
        )}`,
    },
    tokenService: {
      listIdentities: (lifeomicClientId: string, oidcIssuer: string) =>
        lifeomicClientId && oidcIssuer
          ? `/api/v1/identities${stringifyParams({
              clientId: lifeomicClientId,
              issuer: oidcIssuer,
            })}`
          : `/api/v1/identities`,
      addRemoveIdentity: (
        lifeomicClientId: string,
        oidcIssuer: string,
        oidcSubject: string,
      ) =>
        `/api/v1/identities/${lifeomicClientId}/${oidcIssuer}/${oidcSubject}`,
    },
    ema: {
      project: (projectId: string) => `/api/v1/ema/project/${projectId}`,
      patch: (project: string, code: string, version: string) =>
        `/api/v1/ema/${project}/${code}/${version}`,
      create: () => `/api/v1/ema`,
      delete: ({
        project,
        code,
        version,
      }: {
        project: string;
        code: string;
        version: string;
      }) => `/api/v1/ema/${project}/${code}/${version}`,
      uploadImage: () => `/api/v1/ema/upload-image`,
    },
    trialMatching: {
      alerts: (projectId: string, params?: Record<string, string>) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/alerts/me${stringifyParams(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          params,
        )}`,
      alert: (projectId: string, alertId: string) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/alerts/${alertId}/me`,
      favorite: ({
        projectId,
        tagId,
        trialId,
      }: {
        projectId: string;
        tagId: string;
        trialId: string;
      }) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/tags/${tagId}/trials/${trialId}/me`,
      demo: (projectId: string) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/demo`,
      filters: (projectId: string) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/filters`,
      filter: (projectId: string, filterName: string) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/filters/${filterName}`,
      interaction: (projectId: string) =>
        `/api/v1/trial-matching-alert/projects/${projectId}/interactions`,
    },
    trialMatchingNotes: {
      note: (projectId: string, trialId: string) =>
        `/api/v1/trial-matching-note/projects/${projectId}/trials/${trialId}/me`,
    },
    patientML: {
      models: () => '/api/v1/patient-ml/models',
      model: (modelId: string) => `/api/v1/patient-ml/models/${modelId}`,
      examples: (modelId: string, params: Record<string, string>) =>
        `/api/v1/patient-ml/models/${modelId}/examples${stringifyParams(
          params,
        )}`,
      example: (
        projectId: string,
        cohortId: string,
        modelId: string,
        exampleId: string,
      ) =>
        `/api/v1/patient-ml/models/${modelId}/examples/${exampleId}${stringifyParams(
          { projectId, cohortId },
        )}`,
      logs: (modelId: string, runId: string) =>
        `/api/v1/patient-ml/models/${modelId}/runs/${runId}/logs`,
      label: (modelId: string, exampleId: string) =>
        `/api/v1/patient-ml/models/${modelId}/examples/${exampleId}/label`,
      labelFile: (modelId: string, exampleId: string) =>
        `/api/v1/patient-ml/models/${modelId}/examples/${exampleId}/label-file`,
      runs: (modelId: string) => `/api/v1/patient-ml/models/${modelId}/runs`,
    },
    memberRsidQueryService: {
      clinicalGenotype: (project: string, subject: string) =>
        `/api/v1/member-rsid?project=${project}&subject=${subject}`,
    },
    medicalResults: {
      results: (patientId: string, params: object = {}) =>
        `/api/v1/medical-results/patient/${patientId}/results${stringifyParams(
          params,
        )}`,
      resultsV3: (patientId: string, params: object = {}) =>
        `/api/v1/medical-results/v3/patient/${patientId}/results${stringifyParams(
          params,
        )}`,
      history: (patientId: string, params: object = {}) =>
        `/api/v1/medical-results/patient/${patientId}/history${stringifyParams(
          params,
        )}`,
      projectResultCategories: (projectId: string, params: object = {}) =>
        `/api/v1/medical-results/projects/${encodeURIComponent(
          projectId,
        )}/code-mappings/result-categories${stringifyParams(params)}`,
    },
    zoom: {
      appointments: (projectId: Id, params: Record<string, string> = {}) =>
        `/api/v1/zoom/projects/${projectId}/appointments${stringifyParams(
          params,
        )}`,
      reprocessTranscripts: (projectId: Id) =>
        `/api/v1/zoom/projects/${projectId}/reprocess-transcripts`,
      resetAppointment: (projectId: Id, appointmentId: Id) =>
        `/api/v1/zoom/projects/${projectId}/appointments/${appointmentId}/reset`,
    },
  },
  app: {
    home: () => '/home',
    circles: (id?: Id) => `/project/circles${id ? '/' + id : ''}`,
    invitations: () => '/account/accounts/invitations',
    dicomViewer: (fileId: Id) => `/dicom-viewer${fileId ? `/${fileId}` : ''}`,
    files: (params: object) => `/project/files/list${stringifyParams(params)}`,
    manageSurveys: (id?: Id, queryString?: string) =>
      `/project/manage-surveys${id ? `/${id}` : ''}${
        queryString ? `?${queryString}` : ''
      }`,
    surveyResponse: (params?: Record<string, string>) =>
      `/project/survey-response${params ? stringifyParams(params) : ''}`,
    auditing: (params: Record<string, string>) =>
      `/account/auditing${stringifyParams(params)}`,
    trialMatching: {
      root: () => '/project/trial-matching',
      trial: (trial: string, params?: Record<string, string>) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        `/project/trial-matching/${trial}${stringifyParams(params)}`,
    },
    workflows: {
      reportTemplates: (templateId?: string) =>
        `/project/workflows/report-templates${
          templateId ? `/${templateId}` : ''
        }`,
      zoriRules: (
        tab?:
          | 'rules'
          | 'all'
          | 'active'
          | 'inactive'
          | 'knowledge-base'
          | 'clinical-reports'
          | 'codings'
          | 'document-types'
          | 'guidance'
          | 'dex-guidance-review'
          | 'zori-pulse'
          | 'pulse-guidance'
          | 'auto-scan'
          | 'tasks'
          | 'rule-actions'
          | 'document-matchers'
          | 'stats'
          | 'feedback'
          | 'agent-stats'
          | 'user-stats',
        params?: { query?: string; system?: string },
      ) =>
        `/project/workflows/zori-ai${tab ? `/${tab}` : ''}${
          params ? stringifyParams(params) : ''
        }`,
      /**
       * Clinical Reports groups the document matcher, document type, and
       * extraction guidance admin tools under one Zori AI section. Omitting
       * `subTab` lands on the section, which forwards to the first sub tab the
       * viewer is allowed to open.
       */
      zoriClinicalReports: (
        subTab?: 'document-matchers' | 'document-types' | 'extraction-guidance',
      ) =>
        `/project/workflows/zori-ai/clinical-reports${
          subTab ? `/${subTab}` : ''
        }`,
      /**
       * Stats groups feedback review, agent stats, and user stats under one
       * Zori AI section. Omitting `subTab` lands on the section, which forwards
       * to the first sub tab.
       */
      zoriStats: (subTab?: 'feedback' | 'agent-stats' | 'user-stats') =>
        `/project/workflows/zori-ai/stats${subTab ? `/${subTab}` : ''}`,
      /**
       * Zori Pulse groups pulse guidance and pulse auto-scan under one Zori AI
       * section. Omitting `subTab` lands on the section, which forwards to the
       * first sub tab the viewer is allowed to open.
       */
      zoriPulse: (subTab?: 'pulse-guidance' | 'auto-scan') =>
        `/project/workflows/zori-ai/zori-pulse${subTab ? `/${subTab}` : ''}`,
    },
  },
  resources: {
    project: (id: Id) => `/v1/projects/${id}`,
    file: (id: Id) => `/v1/files/${id}`,
    rules: () =>
      'https://support.lifeapps.io/hc/en-us/articles/360016255033-LifeOmic-Content-Guidelines',
  },
  marketplace: {
    domain: () => {
      if (window.location.host.startsWith('localhost:')) {
        return 'https://marketplace.dev.lifeomic.com';
      }
      if (window.location.host.startsWith('lifeomic.localhost:')) {
        return 'https://lifeomic.marketplace.dev.lifeomic.com';
      }

      return `${window.location.protocol}//${window.location.host?.replace(
        'apps.',
        'marketplace.',
      )}/`;
    },
    graphqlEndpoint: () => '/api/v1/marketplace/graphql',
    authenticatedGraphqlEndpoint: () =>
      '/api/v1/marketplace/authenticated/graphql',
  },
  ncbi: {
    gene: (gene: string) => {
      return `https://www.ncbi.nlm.nih.gov/gene?term=(${gene}[Gene Name]) AND human[Organism]`;
    },
    rsid: (rsid?: Id) => `https://www.ncbi.nlm.nih.gov/snp/${rsid}`,
    clinvar: (clinvarAlleleId: Id) =>
      `https://www.ncbi.nlm.nih.gov/clinvar?term=${clinvarAlleleId}[AlleleID`,
  },
};

/**
 * Helpers
 */
export function stringifyParams(params: object, options?: object) {
  return _isEmpty(params) ? '' : `?${queryString.stringify(params, options)}`;
}

interface Params {
  uri: string;
  window: any;
  baseSubdomain?: string;
}

export function baseEnvUrl({ uri, window, baseSubdomain = 'apps' }: Params) {
  const hostParts = window.location.host.split('.');
  const host =
    hostParts.length === 1
      ? hostParts.join('.')
      : hostParts[0] === baseSubdomain && hostParts[1] !== baseSubdomain
      ? hostParts.join('.')
      : hostParts.slice(1).join('.');
  return `${window.location.protocol}//${host}${
    uri.startsWith('/') ? uri : '/' + uri
  }`;
}

export function getHashParams(href: string) {
  if (!href) return null;
  const hashIndex = href.indexOf('#');
  return href
    .substring(hashIndex + 1)
    .split('&')
    .reduce<{ [key: string]: string }>((params, part) => {
      const [key, ...value] = part.split('=');
      params[key] = value.join('=');
      return params;
    }, {});
}

export default {
  ...deepFreeze(urls),
};
